import { Button, Icon, addToast } from '@octano/global-ui';
import { Fragment, FunctionComponent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, FormText, Row, Tooltip } from 'reactstrap';
import isIcon from '../../../../../utils/isIcon';
import { RequestedDocument } from './signDocumentationTypes';

import UploadModal from './UploadModal';

export interface IDocumentCardProps {
  tuitionContinuityProcessId: number | string;
  errorText?: string;
  onDownload: (requestedDocument: RequestedDocument) => Promise<void>;
  onUploaded: (requestedDocument: RequestedDocument) => void;
  onRequestSign?: (requestedDocument: RequestedDocument) => Promise<void>;
  requestedDocument: RequestedDocument;
  requiredDocument?: RequestedDocument | null;
  size: 'md' | 'sm';
  showErrors: boolean;
}
export interface IUrlLinkProps {
  href?: string;
}

export const UrlLink: FunctionComponent<IUrlLinkProps> = ({
  href,
  children,
}) => {
  return (
    <a
      className="fw-600 text-white"
      target="_blank"
      rel="noreferrer"
      href={href}
    >
      {children}
    </a>
  );
};

/**
 * Tarjeta que despliega el documento requerido para ser cargado.
 */
const DocumentCard = ({
  tuitionContinuityProcessId,
  errorText,
  onDownload,
  onRequestSign: onRequestSignProp,
  onUploaded: onUploadedProp,
  requestedDocument,
  requiredDocument,
  size,
  showErrors,
}: IDocumentCardProps) => {
  const prefix = 'tuitionContinuity.signDocumentation';
  const { t } = useTranslation();
  const requiredMsgError = t(`${prefix}.errorRequired`);

  const document = requestedDocument.document;
  const uploaded = requestedDocument.uploaded;
  const downloaded = requestedDocument.downloaded;
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const btnIconName = uploaded ? 'check' : undefined;
  const btnText = `${prefix}.buttons.${uploaded ? 'uploaded' : 'upload'}`;
  const btnSignText = `${prefix}.buttons.${uploaded ? 'signed' : 'sign'}`;
  const btnConfirmTex = `${prefix}.buttons.${
    uploaded ? 'read' : 'confirmReading'
  }`;
  const [open, setOpen] = useState<boolean>(false);
  const closeModal = () => setOpen(false);

  const onUploaded = useCallback(
    (requestedDocument: RequestedDocument) => {
      onUploadedProp(requestedDocument);
      closeModal();
    },
    [onUploadedProp],
  );

  const SignBtn = useCallback(
    () => (
      <Button
        icon={btnIconName}
        onClick={() => {
          if (requiredDocument && !requiredDocument?.downloaded) {
            addToast({
              icon: 'information',
              color: 'warning',
              text: t(`${prefix}.signRequirementRequired`, {
                name: requiredDocument?.document?.name,
                action: t(btnSignText),
              }),
            });
          } else if (downloaded) {
            setOpen(true);
          } else {
            addToast({
              icon: 'information',
              color: 'warning',
              text: t(`${prefix}.signRequirement`),
            });
          }
        }}
        size="sm"
        text={t(btnSignText)}
        fullwidth
        disabled={uploaded}
      />
    ),
    [btnIconName, t, btnSignText, uploaded, requiredDocument, downloaded],
  );
  const ConfirmBtn = useCallback(
    () => (
      <Button
        icon={btnIconName}
        onClick={() => {
          if (requiredDocument && !requiredDocument?.downloaded) {
            addToast({
              icon: 'information',
              color: 'warning',
              text: t(`${prefix}.signRequirementRequired`, {
                name: requiredDocument?.document?.name,
                action: t(btnSignText),
              }),
            });
          } else if (downloaded) {
            setOpen(true);
          } else {
            addToast({
              icon: 'information',
              color: 'warning',
              text: t(`${prefix}.signRequirement`),
            });
          }
        }}
        size="sm"
        text={t(btnConfirmTex)}
        fullwidth
        disabled={uploaded}
      />
    ),
    [
      btnIconName,
      t,
      btnConfirmTex,
      uploaded,
      requiredDocument,
      downloaded,
      btnSignText,
    ],
  );
  const UploadBtn = useCallback(
    () => (
      <Button
        icon={btnIconName}
        onClick={() => {
          if (requiredDocument && !requiredDocument?.downloaded) {
            addToast({
              icon: 'information',
              color: 'warning',
              text: t(`${prefix}.signRequirementRequired`, {
                name: requiredDocument?.document?.name,
                action: t(btnText),
              }),
            });
          } else {
            setOpen(true);
          }
        }}
        size="sm"
        text={t(btnText)}
        fullwidth
      />
    ),
    [btnIconName, btnText, requiredDocument, t],
  );

  return (
    <Fragment>
      <div className="d-flex align-items-end">
        <span className="text-uppercase fs-18 fw-600 text-secondary">
          {document.name}
          {document.required && ' *'}
          {Boolean(document.hint) && (
            <span id={`inputTooltip_${document.code}`} className="pl-2">
              <Icon
                className="cursor-pointer"
                name="information_input"
                size="1rem"
              />
              <Tooltip
                placement="top"
                isOpen={tooltipOpen}
                autohide={false}
                target={`inputTooltip_${document.code}`}
                toggle={() => setTooltipOpen((prevState) => !prevState)}
              >
                {document.hint} <UrlLink />
              </Tooltip>
            </span>
          )}
        </span>
      </div>

      <div className="bg-light px-4 py-3 mb-2" style={{ minHeight: '150px' }}>
        <Row>
          <Col
            className="text-center align-self-center py-3 py-md-1 flex-grow-1"
            xs={12}
            md={size === 'md' ? 6 : 12}
          >
            <Icon
              name={isIcon(document.icon) ? document.icon : 'certificate'}
              size={size === 'md' ? '95px' : '79px'}
              color="secondary"
            />
          </Col>
          <Col
            className="text-center px-5 px-md-3 align-self-end"
            xs={12}
            md={size === 'md' ? 6 : 12}
          >
            {document.generated && (
              <Button
                icon="download"
                onClick={async () => {
                  if (requiredDocument && !requiredDocument?.downloaded) {
                    addToast({
                      icon: 'information',
                      color: 'warning',
                      text: t(`${prefix}.signRequirementRequired`, {
                        name: requiredDocument?.document?.name,
                        action: t(`${prefix}.buttons.download`),
                      }),
                    });
                  } else if (onDownload) {
                    setIsDownloading(true);
                    try {
                      await onDownload(requestedDocument);
                    } finally {
                      setIsDownloading(false);
                    }
                  }
                }}
                outlined
                size="sm"
                text={t(`${prefix}.buttons.download`)}
                fullwidth
                loading={isDownloading}
              />
            )}
            {document.uploadMethod === 'signature' && <SignBtn />}
            {document.uploadMethod === 'default' && <UploadBtn />}
            {document.uploadMethod === 'confirmation' && <ConfirmBtn />}
          </Col>
        </Row>
      </div>

      <FormText
        className="g-input-error"
        color="danger"
        style={{ minHeight: '21px' }}
      >
        {showErrors && !uploaded ? requiredMsgError : null}
      </FormText>
      <UploadModal
        tuitionContinuityProcessId={tuitionContinuityProcessId}
        open={open}
        closeModal={closeModal}
        code={document.code}
        name={document.name}
        description={document.description}
        commentRequired={document.commentRequired}
        uploadMethod={document.uploadMethod}
        onUploaded={() => onUploaded(requestedDocument)}
      />
    </Fragment>
  );
};

export default DocumentCard;
